import { useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useUnmount } from 'react-use';

import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { coreGetOpenApiInfo } from 'CorePackage/MainModule/actions/coreActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';
import { IKnmSearchClipped } from 'APIPackage/KnmSearchModule/interfaces/IKnmSearchClipped';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { Table } from 'CorePackage/TableModule/components/Table';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { TablePage } from 'CorePackage/TableModule/components/TablePage';

import { SearchKnmPanel } from './SearchKnmPanel';
import columnStyles from './KnmSearchTable.module.scss';
import { initialKnmSearchTableColumns } from '../constants/initialKnmSearchTableColumns';
import { initialErknmSearchTableColumns } from '../constants/initialErknmSearchTableColumns';
import {
  knmSearchGetData,
  knmSearchGetDataNextPage,
  knmSearchInit,
  knmSearchResetPage,
  knmSearchSetOrder,
} from '../actions/knmSearchActions';
import { getKnmSearchKnms, getKnmSearchTotalCount } from '../selectors/knmSearchSelectors';

interface IKnmSearchProps {
  isErknm: boolean;
  knms: IKnmSearchClipped[];
  totalCount: number;
}

const KnmSearchWithoutStore = ({ isErknm, knms, totalCount }: IKnmSearchProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [tableColumns, setTableColumns] = useState<ITableColumn<IKnmSearchClipped>[]>(
    isErknm ? initialErknmSearchTableColumns : initialKnmSearchTableColumns,
  );

  useEffect(() => {
    setTableColumns(isErknm ? initialErknmSearchTableColumns : initialKnmSearchTableColumns);
  }, [isErknm]);

  useEffect(() => {
    dispatch(coreGetOpenApiInfo());
    dispatch(knmSearchInit());
  }, [dispatch]);

  useUnmount(() => {
    dispatch(knmSearchResetPage());
  });

  const knmSearchOrder = useCallback(
    (gettingTableColumns?: ITableColumn<IKnmSearchClipped>[]) => {
      const orderTableColumns = gettingTableColumns || tableColumns;

      return getSortOrder(orderTableColumns);
    },
    [tableColumns],
  );

  const handleLoadClick = useCallback(() => {
    dispatch(knmSearchGetDataNextPage());
  }, [dispatch]);

  const handleSortClick = useCallback(
    (id: keyof IKnmSearchClipped) => {
      const newColumnsData = handleSortChange(tableColumns, id);

      setTableColumns(newColumnsData);
      dispatch(knmSearchSetOrder(knmSearchOrder(newColumnsData)));
      dispatch(knmSearchGetData());
    },
    [dispatch, knmSearchOrder, tableColumns],
  );

  return (
    <TablePage>
      <SearchKnmPanel />
      {totalCount > 0 && <div className="shared-title__with-margin">Найдено {totalCount} КНМ</div>}
      <Table<IKnmSearchClipped>
        columns={tableColumns}
        data={knms}
        columnStyles={columnStyles}
        onSortChange={handleSortClick}
        handleLoadClick={handleLoadClick}
        totalCount={totalCount}
      />
    </TablePage>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  knms: getKnmSearchKnms(state),
  totalCount: getKnmSearchTotalCount(state),
});

export const KnmSearch = connect(mapStateToProps)(KnmSearchWithoutStore);
